<script>
import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  DEFAULT_PRIVACY_URL,
  hasAnalyticsConsentDecision,
  isAnalyticsConfigured,
  loadAnalyticsTags,
  recordAnalyticsConsent,
  setAnalyticsConsent,
  syncAnalyticsConsent,
} from 'dashboard/helper/analyticsConsent';

const SIGNUP_ROUTE_NAMES = ['register', 'auth_signup'];

/**
 * #1825 — o portao de consentimento das tags de analise, para quem ja esta dentro da
 * plataforma: o atendente ou admin convidado nunca passa pelo cadastro, e a navegacao dele e
 * medida igual.
 *
 * Mora em `shared/` porque os DOIS apps o montam — o dashboard e o v3 (`/app/login`). Por isso
 * tambem ele nao usa o `Banner.vue` do dashboard: aquele e um filho de layout `flex-col` e so
 * tem um botao de acao; aqui sao dois botoes e uma barra fixa que funciona nos dois lugares.
 *
 * Nao aparece no cadastro: la a pergunta e feita por uma caixa dentro do proprio formulario, e
 * dois pedidos na mesma tela treinam a pessoa a clicar em qualquer coisa para seguir.
 */
export default {
  name: 'AnalyticsConsentBanner',
  props: {
    // v3 (`/app/login`) nao tem sidebar: o banner continua `fixed` cobrindo o rodape da
    // janela, como sempre foi. O dashboard tem sidebar e modais `fixed z-50` por baixo dela
    // (ver comentario no template) e passa `inline` para o banner virar um filho comum do
    // layout em vez de disputar camada com eles.
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasDecision: true,
      isConfigured: false,
    };
  },
  computed: {
    // Instalacao sem `privacyURL` cai na URL publica: um banner que pede consentimento sem
    // dizer onde ler a politica nao e consentimento informado.
    privacyUrl() {
      return (
        this.$store?.getters?.['globalConfig/get']?.privacyURL ||
        DEFAULT_PRIVACY_URL
      );
    },
    // As DUAS rotas do v3 montam o mesmo `Signup/Form.vue` (`routes.js`): `register` e
    // `auth_signup`. Olhar so a primeira deixava o banner aparecer por cima do formulario em
    // `/app/auth/signup`, que e exatamente a tela onde a pergunta ja esta sendo feita.
    isSignupRoute() {
      const route = this.$route || {};
      const path = String(route.path || '');

      return (
        SIGNUP_ROUTE_NAMES.includes(route.name) ||
        path.includes('register') ||
        path.includes('auth/signup')
      );
    },
    shouldShowBanner() {
      return (
        this.isRouteReady &&
        this.isConfigured &&
        !this.hasDecision &&
        !this.isSignupRoute
      );
    },
    // A rota de partida do vue-router 3 (`START`) tem `matched: []`; toda rota resolvida tem
    // pelo menos um registro. `$route` e reativo, entao quando a navegacao inicial confirma
    // este computed recalcula sozinho — sem precisar de `onReady` nem de guardar estado a
    // parte. Se a navegacao falhar, a proxima que confirmar mostra o banner. Sem router, ou
    // com mock sem `matched`, conta como pronto.
    isRouteReady() {
      const route = this.$route;
      return (
        !route || !Array.isArray(route.matched) || route.matched.length > 0
      );
    },
    message() {
      return this.$t('APP_GLOBAL.ANALYTICS_CONSENT.MESSAGE');
    },
    currentUserId() {
      return this.$store?.getters?.getCurrentUser?.id || null;
    },
    bannerClasses() {
      return [
        'flex flex-col gap-3 px-4 py-3 text-sm border-t shadow-lg sm:flex-row sm:items-center sm:justify-between bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100',
        this.inline ? 'shrink-0 w-full' : 'fixed inset-x-0 bottom-0 z-50',
      ];
    },
  },
  watch: {
    // Hoje o `App.vue` do dashboard so renderiza este banner depois do `setUser` (o `v-if` do
    // `#app` espera o `isFetching`), entao o usuario ja esta na store no `mounted`. O watcher e
    // a rede para quando isso mudar: sem usuario o sync so ve `ui_settings` vazio.
    currentUserId(id) {
      if (id) this.reconcile();
    },
  },
  async mounted() {
    this.isConfigured = isAnalyticsConfigured();
    await this.reconcile();
  },
  methods: {
    // Antes de perguntar: quem aceitou no cadastro ou em outro dispositivo ja respondeu.
    async reconcile() {
      await syncAnalyticsConsent(this.$store);
      this.hasDecision = hasAnalyticsConsentDecision();
    },
    accept() {
      setAnalyticsConsent(CONSENT_GRANTED);
      // Carrega agora, sem recarregar a pagina: recarregar perderia o que a pessoa estava
      // fazendo, e o carregador do layout e idempotente.
      loadAnalyticsTags();
      this.decide(CONSENT_GRANTED);
    },
    decline() {
      setAnalyticsConsent(CONSENT_DENIED);
      this.decide(CONSENT_DENIED);
    },
    decide(status) {
      this.hasDecision = true;
      recordAnalyticsConsent(this.$store, status);
      this.$emit('decided', status);
    },
  },
};
</script>

<template>
  <!--
    #1852 — nenhum numero de z-index resolve: como overlay `fixed` o banner disputa camada
    com a sidebar e com todo modal/painel `fixed z-50` que vem depois no DOM (franquia,
    kanban, preview de empresa, popup de tarefas, overlay de redimensionar coluna da
    importacao, rodape da sidebar). No dashboard ele entra no fluxo (`inline`), como filho
    comum do `flex-col` do `#app`, e ocupa o rodape da janela sem cobrir nada.
  -->
  <div
    v-if="shouldShowBanner"
    :class="bannerClasses"
    role="region"
    :aria-label="$t('APP_GLOBAL.ANALYTICS_CONSENT.TITLE')"
  >
    <p class="m-0 sm:max-w-[72%]">
      {{ message }}
      <a
        :href="privacyUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="font-medium underline text-woot-600 dark:text-woot-300"
      >
        {{ $t('APP_GLOBAL.ANALYTICS_CONSENT.PRIVACY_LINK') }}
      </a>
    </p>
    <div class="flex items-center gap-2 shrink-0">
      <button
        type="button"
        class="px-3 py-2 font-medium border rounded-md border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700"
        @click="decline"
      >
        {{ $t('APP_GLOBAL.ANALYTICS_CONSENT.DECLINE') }}
      </button>
      <button
        type="button"
        class="px-3 py-2 font-medium text-white rounded-md bg-woot-600 hover:bg-woot-700"
        @click="accept"
      >
        {{ $t('APP_GLOBAL.ANALYTICS_CONSENT.ACCEPT') }}
      </button>
    </div>
  </div>
</template>
